import React, { useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";

interface TutorProfileReview {
  _id: string;
  rating: number;
  comment: string;
  parentDisplayName: string;
  createdAt: number;
}

interface TutorProfileReviewsListProps {
  reviews: TutorProfileReview[];
}

const COLLAPSED_COMMENT_LINES = 4;
const COMMENT_EXPAND_THRESHOLD = 140;

function formatReviewDate(timestamp: number) {
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}

function StarRatingDisplay({ rating }: { rating: number }) {
  return (
    <View className="flex-row items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Text
          key={star}
          className={`text-sm ${star <= rating ? "text-amber-500" : "text-gray-300"}`}
        >
          ★
        </Text>
      ))}
    </View>
  );
}

function TutorProfileReviewsList({ reviews }: TutorProfileReviewsListProps) {
  const [expandedReviewIds, setExpandedReviewIds] = useState<Set<string>>(
    new Set(),
  );

  if (reviews.length === 0) return null;

  function toggleReview(reviewId: string) {
    setExpandedReviewIds((prev) => {
      const next = new Set(prev);
      if (next.has(reviewId)) next.delete(reviewId);
      else next.add(reviewId);
      return next;
    });
  }

  return (
    <View className="gap-2 pb-2">
      <Text className="font-geologica-medium text-base text-black leading-[1.5]">
        Reviews
      </Text>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerClassName="gap-3 px-1 pt-1 pb-4"
        style={{ overflow: "visible" }}
      >
        {reviews.map((review) => {
          const isExpanded = expandedReviewIds.has(review._id);
          const canExpand = review.comment.length > COMMENT_EXPAND_THRESHOLD;

          return (
            <Pressable
              key={review._id}
              disabled={!canExpand}
              onPress={() => toggleReview(review._id)}
              className="w-[280px] gap-3 rounded-xl bg-white p-4 shadow-sm"
            >
              <View className="flex-row items-center justify-between gap-2">
                <StarRatingDisplay rating={review.rating} />
                <Text className="font-geologica-regular text-xs text-gray-500">
                  {formatReviewDate(review.createdAt)}
                </Text>
              </View>

              <View className="gap-1">
                <Text
                  className="font-geologica-regular text-sm text-dark leading-[1.5]"
                  numberOfLines={
                    isExpanded ? undefined : COLLAPSED_COMMENT_LINES
                  }
                >
                  {review.comment}
                </Text>
                {canExpand && (
                  <Text className="font-geologica-medium text-sm text-teal-700">
                    {isExpanded ? "Show less" : "Read more"}
                  </Text>
                )}
              </View>

              <Text className="font-geologica-medium text-sm text-gray-800">
                {review.parentDisplayName}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

export default TutorProfileReviewsList;
